/**
 * Submit-only required-section validation for the unified intake form.
 *
 * Per the PRD (FR-D6.1) and the workflow Decision Ledger (D21): a fixed set of
 * sections must be non-empty **at final submit**, but prose is **never**
 * validated mid-call. This helper is intentionally pure and is invoked only
 * from the final-submit handler (never on step navigation), so a slow scribe
 * draft or an in-progress call never blocks the clinician between steps.
 *
 * "Non-empty at submit regardless of source" (D21): a value counts whether it
 * was AI-drafted, prefilled, or typed manually.
 *
 * @agentic connections
 */

import type { IntakeInteractionFormData } from "@benchmark/core/types";
import { NOTE_SECTIONS } from "../note-sections";

/**
 * Retired legacy field keys for a note section, sourced from `NOTE_SECTIONS`, so
 * the required-section gate and the note rendering agree on what a section's
 * historical content is. Empty for sections that never had a retired generic.
 */
const legacyKeysForSection = (sectionId: string): string[] =>
  NOTE_SECTIONS.find((section) => section.id === sectionId)?.legacyFieldKeys ??
  [];

const MENTAL_STATUS_KEYS = [
  "mental-status-appearance",
  "mental-status-behavior",
  "mental-status-speech",
  "mental-status-mood-affect",
  "mental-status-thought-process-content",
  "mental-status-perception",
  "mental-status-cognition",
  "mental-status-insight-judgment",
] as const;

const nonEmpty = (value: unknown): boolean =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Returns the display labels of required intake sections that are still empty
 * at submit. An empty array means the intake may be submitted. The set is
 * Chief Complaint, History of Present Illness, hospitalization history,
 * Current Outpatient Clinicians, and Mental Status Exam.
 *
 * Goals are intentionally NOT required at submit. Although the PRD (FR-D6.1 /
 * ledger D12) originally listed Goals in the required set, clinicians reported
 * that goals are frequently not established during intake, so blocking submit
 * on a missing goal prevented saving otherwise-complete notes. The Goals step
 * remains available and is marked optional in the form's step navigation.
 * @agentic connections
 */
export const getMissingRequiredIntakeSections = (
  values: IntakeInteractionFormData,
): string[] => {
  const missing: string[] = [];

  if (!nonEmpty(values["chief-complaint"])) missing.push("Chief Complaint");
  if (!nonEmpty(values["hpi-current-symptoms"]))
    missing.push("History of Present Illness");
  if (!nonEmpty(values["hospitalizations"])) missing.push("Hospitalizations");

  // Uniform with the other note gates: a required section is satisfied by
  // content in its authored key OR any retired legacy key for that section. The
  // authored "existing treaters" field satisfies this on a new intake (no legacy
  // content exists), and a historical encounter documented under the retired
  // "current outpatient treaters" key no longer reads as incomplete on edit.
  const outpatientTreatersKeys = [
    "existing-treaters",
    ...legacyKeysForSection("current-outpatient-treaters"),
  ];
  const hasOutpatientTreaters = outpatientTreatersKeys.some((key) =>
    nonEmpty((values as Record<string, unknown>)[key]),
  );
  if (!hasOutpatientTreaters) {
    missing.push("Current Outpatient Clinicians");
  }

  // MSE is satisfied when any domain has content (telephonic intakes seed
  // Appearance/Perception with a "not inferable" default, so this is normally
  // met without blocking).
  const hasMentalStatus = MENTAL_STATUS_KEYS.some((key) =>
    nonEmpty((values as Record<string, unknown>)[key]),
  );
  if (!hasMentalStatus) missing.push("Mental Status Exam");

  return missing;
};
